const STORAGE_KEY = 'countryLanguageSwitcherConfirmed';

class CountryLanguageSwitcher extends HTMLElement {
  constructor() {
    super();
    
    this.overlay = this.querySelector('.cls-overlay');
    this.modal = this.querySelector('.cls-modal');
    this.closeButton = this.querySelector('.cls-close');
    this.form = this.querySelector('.cls-form');
    this.countryInput = this.querySelector('input[name="country_code"]');
    this.languageInput = this.querySelector('input[name="language_code"]');
    this.dropdowns = Array.from(this.querySelectorAll('[data-dropdown]'));
    this.countryDropdown = this.querySelector('[data-dropdown="country"]');
    
    this.autoOpen = this.dataset.autoOpen === 'true';
    this.showLanguage = this.dataset.showLanguage === 'true';
    
    this.bindEvents();
    this.checkAutoOpen();
  }

  bindEvents() {
    // Fermer avec le bouton X
    this.closeButton?.addEventListener('click', () => this.close());
    
    // Fermer en cliquant sur l'overlay
    this.overlay?.addEventListener('click', () => this.close());
    
    // Fermer avec Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.hasOpenDropdown()) {
          this.closeAllDropdowns();
        } else if (this.isOpen()) {
          this.close();
        }
      }
    });

    // Soumission du formulaire
    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));

    // Écouter les déclencheurs externes
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-country-switcher]');
      if (trigger) {
        e.preventDefault();
        this.open();
      }
    });

    // Custom dropdown events
    this.dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector('.cls-dropdown-trigger');
      const list = dropdown.querySelector('.cls-dropdown-list');
      trigger?.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleDropdown(dropdown);
      });
      list?.addEventListener('click', (e) => this.selectDropdownItem(dropdown, e));
    });

    // Fermer dropdown au clic extérieur
    document.addEventListener('click', (e) => this.closeDropdownOnClickOutside(e));
  }

  async checkAutoOpen() {
    if (!this.autoOpen) return;
    
    const hasConfirmed = localStorage.getItem(STORAGE_KEY);
    if (hasConfirmed) return;

    // Attendre que la page soit chargée
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initWithGeoIP());
    } else {
      this.initWithGeoIP();
    }
  }

  async initWithGeoIP() {
    try {
      // Récupérer le pays détecté par GeoIP
      const response = await fetch('/browsing_context_suggestions.json');
      const data = await response.json();
      const detectedCountry = data.detected_values?.country?.handle;

      // Pré-sélectionner le pays détecté si disponible
      const countryList = this.countryDropdown?.querySelector('.cls-dropdown-list');
      if (detectedCountry && countryList) {
        const item = countryList.querySelector(`[data-value="${detectedCountry}"]`);
        if (item) {
          this.setDropdownSelection(this.countryDropdown, item);
        }
      }
    } catch (error) {
      console.warn('GeoIP detection failed:', error);
    }

    // Petit délai pour laisser la page se charger visuellement
    setTimeout(() => this.open(), 500);
  }

  open() {
    this.overlay?.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('is-visible');
    
    this.modal?.setAttribute('aria-hidden', 'false');
    this.modal?.classList.add('is-visible');
    
    document.body.classList.add('overflow-hidden');
    
    // Focus sur le premier élément interactif
    this.closeButton?.focus();
  }

  close() {
    this.overlay?.setAttribute('aria-hidden', 'true');
    this.overlay?.classList.remove('is-visible');
    
    this.modal?.setAttribute('aria-hidden', 'true');
    this.modal?.classList.remove('is-visible');
    
    document.body.classList.remove('overflow-hidden');
  }

  isOpen() {
    return this.modal?.getAttribute('aria-hidden') === 'false';
  }

  handleSubmit(e) {
    // Sauvegarder le choix
    const countryCode = this.countryInput?.value;
    const languageCode = this.languageInput?.value;
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      date: Date.now(),
      country: countryCode,
      language: languageCode
    }));

    // Le formulaire se soumet normalement et recharge la page
    // avec le nouveau contexte de localisation
  }

  // Custom Dropdown Methods
  toggleDropdown(dropdown) {
    if (!dropdown) return;
    if (this.isDropdownOpen(dropdown)) {
      this.closeDropdown(dropdown);
    } else {
      this.closeAllDropdowns(dropdown);
      this.openDropdown(dropdown);
    }
  }

  openDropdown(dropdown) {
    const trigger = dropdown?.querySelector('.cls-dropdown-trigger');
    dropdown?.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
  }

  closeDropdown(dropdown) {
    const trigger = dropdown?.querySelector('.cls-dropdown-trigger');
    dropdown?.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
  }

  closeAllDropdowns(exceptDropdown = null) {
    this.dropdowns.forEach((dropdown) => {
      if (dropdown !== exceptDropdown) {
        this.closeDropdown(dropdown);
      }
    });
  }

  isDropdownOpen(dropdown) {
    return dropdown?.classList.contains('is-open');
  }

  hasOpenDropdown() {
    return this.dropdowns.some((dropdown) => this.isDropdownOpen(dropdown));
  }

  closeDropdownOnClickOutside(e) {
    if (!this.contains(e.target)) return;
    const clickedInsideAnyDropdown = this.dropdowns.some((dropdown) => dropdown.contains(e.target));
    if (!clickedInsideAnyDropdown) {
      this.closeAllDropdowns();
    }
  }

  selectDropdownItem(dropdown, e) {
    const item = e.target.closest('.cls-dropdown-item');
    if (!item) return;
    this.setDropdownSelection(dropdown, item);
    this.closeDropdown(dropdown);
  }

  setDropdownSelection(dropdown, item) {
    const value = item.dataset.value;
    const text = item.textContent.trim();
    const valueSpan = dropdown?.querySelector('[data-dropdown-value]');
    const hiddenInput = dropdown?.querySelector('[data-dropdown-input]');
    const list = dropdown?.querySelector('.cls-dropdown-list');

    // Mettre à jour l'input hidden
    if (hiddenInput) {
      hiddenInput.value = value;
    }

    // Mettre à jour le texte du trigger
    if (valueSpan) {
      valueSpan.textContent = text;
    }

    // Mettre à jour aria-selected
    list?.querySelectorAll('[aria-selected]').forEach(el => {
      el.removeAttribute('aria-selected');
    });
    item.setAttribute('aria-selected', 'true');
  }
}

customElements.define('country-language-switcher', CountryLanguageSwitcher);
